import React, { useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { EmptyState, tableEmpty } from '../common/PageHeader';
import TableSearch, { filtriraj } from '../common/TableSearch';
import { akcizeApi } from '../../api/services';
import { useAlerts } from '../../custom/Alerts';
import { datum, lit, km, din, mesecIzBroja } from './format';

const uBase64 = (file) => new Promise((ok, greska) => { const r = new FileReader(); r.onload = () => ok(r.result); r.onerror = greska; r.readAsDataURL(file); });

/* Uvoz Excel evidencija koje firma već vodi ("Nabavka goriva", "Tabela vozila"): prvo pregled (ništa se ne upisuje),
   sa upozorenjima — verzije istog meseca, sumnjivi datumi, nepoznata vozila; zatim snimanje. */
export default function Uvoz({ companyId, onUvezeno }) {
    const alerts = useAlerts();
    const ref = useRef(null);
    const [pregled, setPregled] = useState(null);
    const [pretraga, setPretraga] = useState('');
    const stavke = useMemo(() => (pregled ? filtriraj(pregled.stavke, pretraga, (s) => (pregled.vrsta === 'nabavke'
        ? [datum(s.datum), s.brojRacuna, lit(s.kolicina), s.dobavljac, din(s.iznos), s.brojIzvoda, s.list, s.upozorenje]
        : [`${mesecIzBroja(s.mesec)} ${s.godina}`, s.reg, s.vozilo || 'nije u kompaniji', lit(s.litara), km(s.kmPrijavljeno), s.dokument])) : []), [pregled, pretraga]);
    const [radi, setRadi] = useState(false);
    const [zameni, setZameni] = useState(false);

    const izaberi = async (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        setRadi(true); setPregled(null);
        try { setPregled(await akcizeApi.analiza({ companyId, ime: f.name, data: await uBase64(f) })); } catch { /* toast */ }
        setRadi(false);
    };

    const zaUvoz = useMemo(() => {
        if (!pregled) return [];
        return pregled.vrsta === 'vozila' ? pregled.stavke.filter((s) => s.vehicleId) : pregled.stavke;
    }, [pregled]);

    const uvezi = async () => {
        setRadi(true);
        try {
            const r = pregled.vrsta === 'nabavke'
                ? await akcizeApi.uvozNabavke({ companyId, stavke: zaUvoz })
                : await akcizeApi.uvozIzdavanja({ companyId, zameni, stavke: zaUvoz });
            alerts.success(`Uvezeno: ${r.dodato}${r.zamenjeno ? `, zamenjeno: ${r.zamenjeno}` : ''}, već postoji (preskočeno): ${r.postoji}.`, 'Uvoz');
            setPregled(null);
            onUvezeno();
        } catch { /* toast */ }
        setRadi(false);
    };

    const sumnjivi = pregled && pregled.vrsta === 'nabavke' ? pregled.stavke.filter((s) => s.upozorenje) : [];
    const ukupnoL = zaUvoz.reduce((a, s) => a + (pregled.vrsta === 'nabavke' ? s.kolicina : s.litara), 0);

    return (
        <div className="akc-uvoz">
            <div className="akc-uvoz-traka">
                <Button label="Izaberi Excel fajl" icon="pi pi-file-excel" onClick={() => ref.current && ref.current.click()} loading={radi && !pregled} />
                <input ref={ref} type="file" accept=".xls,.xlsx" hidden onChange={izaberi} />
                <span className="muted">Evidencija nabavke goriva ili tabela vozila po mesecima (.xls ili .xlsx). Prvo se prikazuje pregled — ništa se ne upisuje bez potvrde.</span>
            </div>

            {!pregled && !radi && <EmptyState icon="pi pi-upload" title="Uvoz postojećih evidencija" text="Nabavke se vezuju za izabranu kompaniju; vozila se prepoznaju po registarskoj oznaci." />}

            {pregled && (
                <section className="panel">
                    <div className="panel-head">
                        <h3 className="panel-title">
                            {pregled.ime} — {pregled.vrsta === 'nabavke' ? 'nabavke goriva' : 'gorivo po vozilu i mesecu'}
                        </h3>
                        <div className="page-actions">
                            {pregled.vrsta === 'vozila' && (
                                <span className="akc-check"><Checkbox inputId="akc-zameni" checked={zameni} onChange={(e) => setZameni(e.checked)} />
                                    <label htmlFor="akc-zameni">Zameni postojeće mesece</label></span>
                            )}
                            <Button label="Otkaži" text severity="secondary" onClick={() => setPregled(null)} />
                            <Button label={`Uvezi ${zaUvoz.length} stavki`} icon="pi pi-check" onClick={uvezi} loading={radi} disabled={!zaUvoz.length} />
                        </div>
                    </div>
                    <p className="muted">Listova: {pregled.listovi.length} · stavki za uvoz: {zaUvoz.length} · ukupno {lit(ukupnoL)} L</p>

                    <ul className="akc-lista">
                        {(pregled.verzije || []).map((v) => (
                            <li key={`${v.godina}-${v.mesec}`} className="akc-stavka warn"><i className="pi pi-exclamation-triangle" aria-hidden="true" />
                                <span>{mesecIzBroja(v.mesec)} {v.godina}: više verzija lista — korišćen „{v.koriscen}“, preskočeno: {v.preskoceni.map((x) => `„${x}“`).join(', ')}. Proverite koja je tačna.</span></li>
                        ))}
                        {(pregled.duplikati || []).length > 0 && (
                            <li className="akc-stavka warn"><i className="pi pi-exclamation-triangle" aria-hidden="true" />
                                <span>{pregled.duplikati.length} računa se ponavlja u više listova (uvozi se jednom): {pregled.duplikati.slice(0, 6).map((d) => `${d.brojRacuna} (${datum(d.datum)}: ${d.listovi.join(', ')}${d.razlicitaKolicina ? ', RAZLIČITA KOLIČINA' : ''})`).join('; ')}{pregled.duplikati.length > 6 ? '…' : ''}.</span></li>
                        )}
                        {sumnjivi.map((s, i) => (
                            <li key={`s${i}`} className="akc-stavka warn"><i className="pi pi-exclamation-triangle" aria-hidden="true" />
                                <span>{s.upozorenje} — račun {s.brojRacuna}, {lit(s.kolicina)} L.</span></li>
                        ))}
                        {(pregled.nepoznata || []).length > 0 && (
                            <li className="akc-stavka danger"><i className="pi pi-times-circle" aria-hidden="true" />
                                <span>Vozila kojih nema u ovoj kompaniji (ne uvoze se dok se ne dodaju u Vozila): {pregled.nepoznata.join(', ')}.</span></li>
                        )}
                        {pregled.listovi.filter((l) => l.preskocen).map((l) => (
                            <li key={l.list} className="akc-stavka info"><i className="pi pi-info-circle" aria-hidden="true" /><span>List „{l.list}“ preskočen: {l.preskocen}.</span></li>
                        ))}
                    </ul>

                    <TableSearch value={pretraga} onChange={setPretraga} placeholder="Pretraga stavki (račun, registracija, dobavljač, list...)" label="Pretraga stavki uvoza"
                        ukupno={pregled.stavke.length} pronadjeno={stavke.length} jedinica="stavki" />
                    {pregled.vrsta === 'nabavke' ? (
                        <DataTable value={stavke} emptyMessage={tableEmpty('Nijedna stavka ne odgovara pretrazi')} size="small" stripedRows paginator rows={15} rowClassName={(s) => (s.upozorenje ? 'akc-red-upozorenje' : '')}>
                            <Column header="Datum" body={(s) => datum(s.datum)} sortable sortField="datum" />
                            <Column field="brojRacuna" header="Broj računa" />
                            <Column header="Količina (L)" body={(s) => lit(s.kolicina)} className="num" />
                            <Column field="dobavljac" header="Dobavljač" />
                            <Column header="Iznos" body={(s) => din(s.iznos)} className="num" />
                            <Column field="brojIzvoda" header="Izvod" />
                            <Column field="list" header="List" />
                        </DataTable>
                    ) : (
                        <DataTable value={stavke} emptyMessage={tableEmpty('Nijedna stavka ne odgovara pretrazi')} size="small" stripedRows paginator rows={15} rowClassName={(s) => (s.vehicleId ? '' : 'akc-red-nepoznato')}
                            sortField="godina" sortOrder={-1}>
                            <Column header="Mesec" body={(s) => `${mesecIzBroja(s.mesec)} ${s.godina}`} sortable sortField="godina" />
                            <Column field="reg" header="Reg. oznaka" sortable />
                            <Column header="Vozilo" body={(s) => s.vozilo || <span className="akc-iznad">nije u kompaniji</span>} />
                            <Column header="Litara" body={(s) => lit(s.litara)} className="num" />
                            <Column header="Km (prijavljeno)" body={(s) => km(s.kmPrijavljeno)} className="num" />
                            <Column field="dokument" header="List(ovi)" />
                        </DataTable>
                    )}
                </section>
            )}
        </div>
    );
}
